/**
 * src/features/auth/lib/authBroadcast.ts
 *
 * Phase 43 (PWD-06) — the second message type on the app's one and only cross-tab
 * BroadcastChannel (`estimatix-auth`, live since Phase 32 for `auth-success`). This file is
 * the single source of truth for the channel name and the two-member discriminated union
 * that rides on it — CLAUDE.md rule 7 forbids modeling this as `{ type?: string }`, which is
 * exactly what a second message type would have made non-compliant.
 *
 * A React-free, pure-function module over the browser's own APIs — the same discipline
 * lastLoginMethod.ts follows — is what makes the union shape, the payload contents, and the
 * self-origin predicate unit-testable without the component stack.
 *
 * Why a per-tab origin id exists at all (verified, not hypothetical): BroadcastChannel does
 * NOT deliver a message back to the exact channel *object* that posted it, but it DOES
 * deliver to every *other* channel object in the same tab (see the comment recording this for
 * the existing `auth-success` sender in useAuth.ts). Two components mount `useAuth()` in this
 * app (AuthInit, MagicLinkForm), so a tab can hold two independent listeners at once. Without
 * a per-tab discriminator, the tab that just changed its own password would show ITSELF a
 * "you've been signed out" notice — false and alarming, and the exact opposite of what
 * changePassword's freshly rotated session means for that tab. A consume-once boolean would
 * not fix this: the first listener would eat it and the second would fire anyway. The origin
 * id is independent of listener count and needs no timers, so tests relying on it stay
 * deterministic (43-04 flagged_assumptions item 3).
 */

/** The one and only cross-tab auth channel — do not open a second one. Value pinned to what
 * the existing sender and listener in useAuth.ts already use; a different spelling here would
 * silently break every message on this channel while mocked tests kept passing. */
export const AUTH_BROADCAST_CHANNEL = 'estimatix-auth';

/** Discriminated union, not `{ type?: string }` (CLAUDE.md rule 7) — a second message type is
 * exactly what makes the old loosely-typed shape non-compliant. `session-revoked` carries an
 * opaque per-tab origin id and nothing else: no session token, no email, no user content
 * (T-43-20). */
export type AuthBroadcastMessage =
  | { type: 'auth-success' }
  | { type: 'session-revoked'; origin: string };

function generateOriginId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Non-crypto fallback for environments lacking crypto.randomUUID. This id is not a secret
  // and carries no identity (T-43-20) — it only has to be unique-enough within one tab's
  // lifetime to distinguish "this tab" from "some other tab", so Math.random is fine here.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// Generated once at module evaluation — stable for the lifetime of this tab/module instance,
// which is exactly the scope the self-origin guard needs (T-43-21).
const ORIGIN_ID = generateOriginId();

/** Sends the session-revoked message on the shared channel: create -> post -> close, the same
 * shape the existing auth-success sender uses. Fire-and-forget by design — the caller (a
 * reset-success or change-password-success path) does not wait for or need a response. */
export function broadcastSessionRevoked(): void {
  const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
  const message: AuthBroadcastMessage = { type: 'session-revoked', origin: ORIGIN_ID };
  channel.postMessage(message);
  channel.close();
}

/** Whether a received `session-revoked` message's origin equals this module instance's own
 * origin id — i.e. whether THIS tab is the one that caused the revocation. Stateless: calling
 * it twice for the same message returns the same answer both times (T-43-21 — no consume-once
 * flag, which a second `useAuth()` listener in the same tab would otherwise eat). */
export function isSelfOrigin(origin: string | undefined): boolean {
  return origin === ORIGIN_ID;
}
